import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '@/hooks/use-toast';
import { Save, FileText, Link as LinkIcon, Users, Trash2, UserPlus } from 'lucide-react';
import { CATEGORY_CONFIGS, AVAILABLE_CATEGORIES } from '@/constants/categories';
import type { ProductCategory } from '@/types';
import axios from 'axios';
import { useSession } from '@/lib/authClient';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

interface Settings {
  id: number;
  masterPdfTemplateUrl: string | null;
  categoryReadmePdfs: Record<ProductCategory, string | undefined>;
  bunnyApiKey: string | null;
  mockupServiceUrl: string | null;
  mockupServiceSecret: string | null;
  googleDriveFolderId: string | null;
}

interface UserAccount {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  createdAt: string;
}

/**
 * SettingsPage Component
 * 
 * Manages application settings:
 * - Master PDF template URL (delivery PDF)
 * - Category-specific README PDF URLs
 */
export function SettingsPage() {
  const [masterPdfUrl, setMasterPdfUrl] = useState('');
  const [readmePdfUrls, setReadmePdfUrls] = useState<Record<ProductCategory, string>>({
    'digital-art-print': '',
    'tv-art-16-9': '',
    'triptych': '',
    'diptych': '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const { toast } = useToast();
  const { data: session } = useSession();

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get<{ success: boolean; data: Settings }>(
        `${window.location.origin}/api/settings`
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setMasterPdfUrl(data.masterPdfTemplateUrl || '');
        
        // Load category README PDFs
        const readmes: Record<ProductCategory, string> = {
          'digital-art-print': data.categoryReadmePdfs?.['digital-art-print'] || '',
          'tv-art-16-9': data.categoryReadmePdfs?.['tv-art-16-9'] || '',
          'triptych': data.categoryReadmePdfs?.['triptych'] || '',
          'diptych': data.categoryReadmePdfs?.['diptych'] || '',
        };
        setReadmePdfUrls(readmes);
      }
    } catch (error) {
      toast({
        title: 'Failed to Load Settings',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axios.get<{ success: boolean; data: UserAccount[] }>(
        `${window.location.origin}/api/users`
      );
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      toast({
        title: 'Failed to Load Users',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [toast]);

  const handleDeleteUser = async (userId: string) => {
    if (userId === session?.user?.id) {
      toast({
        title: 'Cannot Delete',
        description: 'You cannot delete your own account while logged in',
        variant: 'destructive',
      });
      return;
    }

    setDeletingUserId(userId);
    try {
      const response = await axios.delete(`${window.location.origin}/api/users`, {
        data: { userId },
      });
      if (response.data.success) {
        toast({
          title: 'User Deleted',
          description: 'The user account has been removed',
        });
        await loadUsers();
      }
    } catch (error) {
      toast({
        title: 'Failed to Delete User',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword) {
      toast({
        title: 'Missing Fields',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    if (newUserPassword.length < 8) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 8 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingUser(true);
    try {
      const response = await axios.post(`${window.location.origin}/api/users`, {
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        password: newUserPassword,
      });
      if (response.data.success) {
        toast({
          title: 'Account Created',
          description: `Account for ${newUserEmail.trim()} has been created`,
        });
        setNewUserName('');
        setNewUserEmail('');
        setNewUserPassword('');
        await loadUsers();
      }
    } catch (error: any) {
      toast({
        title: 'Failed to Create Account',
        description: error.response?.data?.error || error.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadUsers();
  }, [loadSettings, loadUsers]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Build category README PDFs object (only include non-empty URLs)
      const categoryReadmePdfs: Record<string, string> = {};
      Object.entries(readmePdfUrls).forEach(([category, url]) => {
        if (url.trim()) {
          categoryReadmePdfs[category] = url.trim();
        }
      });

      const response = await axios.put(
        `${window.location.origin}/api/settings`,
        {
          masterPdfTemplateUrl: masterPdfUrl.trim() || null,
          categoryReadmePdfs,
        }
      );

      if (response.data.success) {
        toast({
          title: 'Settings Saved',
          description: 'Your settings have been updated successfully',
        });
        await loadSettings(); // Reload to get updated data
      }
    } catch (error) {
      toast({
        title: 'Failed to Save Settings',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Settings</h2>
        <p className="text-muted-foreground">
          Configure PDF templates and category-specific README files
        </p>
      </div>

      {/* Master PDF Template */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Master PDF Template
          </CardTitle>
          <CardDescription>
            URL to the delivery PDF template that will be customized for each product
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="master-pdf">PDF Template URL</Label>
            <Input
              id="master-pdf"
              type="url"
              placeholder="https://example.com/template.pdf"
              value={masterPdfUrl}
              onChange={(e) => setMasterPdfUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This PDF will be used as the base template for customer delivery documents
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Category README PDFs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Category README PDFs
          </CardTitle>
          <CardDescription>
            URLs to README PDFs that will be automatically uploaded to Google Drive for each category
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {AVAILABLE_CATEGORIES.map((category) => {
            const config = CATEGORY_CONFIGS[category];
            return (
              <div key={category} className="space-y-2">
                <Label htmlFor={`readme-${category}`}>
                  {config.label}
                </Label>
                <Input
                  id={`readme-${category}`}
                  type="url"
                  placeholder="https://example.com/readme.pdf"
                  value={readmePdfUrls[category]}
                  onChange={(e) => setReadmePdfUrls(prev => ({
                    ...prev,
                    [category]: e.target.value,
                  }))}
                />
                <p className="text-xs text-muted-foreground">
                  {config.description}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Account Manager */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Account Manager
          </CardTitle>
          <CardDescription>
            Create and manage user accounts. Public registration is disabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleCreateUser} className="space-y-4 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="h-4 w-4" />
              <span className="font-medium text-sm">Create New Account</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="new-user-name">Name</Label>
                <Input
                  id="new-user-name"
                  placeholder="John Doe"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-email">Email</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-password">Password</Label>
                <Input
                  id="new-user-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Min 8 characters"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" disabled={isCreatingUser} size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              {isCreatingUser ? 'Creating...' : 'Create Account'}
            </Button>
          </form>

          <div className="space-y-3">
            <span className="font-medium text-sm">Existing Accounts</span>
            {isLoadingUsers ? (
              <p className="text-muted-foreground text-sm">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-muted-foreground text-sm">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Created: {new Date(u.createdAt).toLocaleDateString()}
                        {u.id === session?.user?.id && (
                          <span className="ml-2 text-primary">(You)</span>
                        )}
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={u.id === session?.user?.id || deletingUserId === u.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete User Account</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the account for{' '}
                            <strong>{u.email}</strong>? This action cannot be undone.
                            All sessions and data associated with this user will be removed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteUser(u.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

